import { useState, useCallback } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import axios from 'axios';
import logger from '../utils/logger';

export const useApi = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const { getAccessTokenSilently, isAuthenticated } = useAuth0();

  const makeRequest = useCallback(async (apiCall, options = {}) => {
    const { 
      showLoading = true, 
      retries = 1, 
      onSuccess, 
      onError,
      requireAuth = true 
    } = options;

    if (showLoading) setLoading(true);
    setError(null);

    let attempt = 0;
    
    while (attempt <= retries) {
      try {
        // Configurer l'authentification si nécessaire
        if (requireAuth && isAuthenticated) {
          const token = await getAccessTokenSilently({
            audience: process.env.REACT_APP_AUTH0_AUDIENCE,
            scope: "openid profile email"
          });
          axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
        }

        const response = await apiCall();
        
        if (showLoading) setLoading(false);
        
        if (onSuccess) {
          onSuccess(response.data);
        }
        
        return { data: response.data, success: true };
        
      } catch (err) {
        attempt++;
        
        if (attempt > retries) {
          const errorMessage = err.response?.data?.message || err.message || 'Une erreur est survenue';
          
          logger.error('API Error:', {
            url: err.config?.url,
            method: err.config?.method,
            status: err.response?.status,
            message: errorMessage
          });
          
          setError({
            message: errorMessage,
            status: err.response?.status,
            code: err.code
          });
          
          if (showLoading) setLoading(false);
          
          if (onError) {
            onError(err);
          }
          
          return { error: errorMessage, success: false };
        }
        
        // Attendre avant de réessayer
        if (attempt <= retries) {
          const delay = 1000 * attempt;
          await new Promise(resolve => setTimeout(() => resolve(), delay));
        }
      }
    }
  }, [getAccessTokenSilently, isAuthenticated]);

  const get = useCallback((url, options = {}) => {
    return makeRequest(() => axios.get(url), options);
  }, [makeRequest]);

  const post = useCallback((url, data, options = {}) => {
    return makeRequest(() => axios.post(url, data), options);
  }, [makeRequest]);

  const put = useCallback((url, data, options = {}) => {
    return makeRequest(() => axios.put(url, data), options);
  }, [makeRequest]);

  const del = useCallback((url, options = {}) => {
    return makeRequest(() => axios.delete(url), options);
  }, [makeRequest]);

  const clearError = useCallback(() => {
    setError(null);
  }, []);

  return {
    loading,
    error,
    makeRequest,
    get,
    post,
    put,
    delete: del,
    clearError
  };
};

export default useApi;
